import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'

import OrderDetailAdaptive from '../components/OrderDetailAdaptive'
import { markViewed, takeOrder, submitPrice, rejectOrder, shareSingleOrderFile } from '../../shared/api/endpoints'
import OrderActionsApi from '../../shared/api/orderActionsApi'
import { http } from '../../shared/api/http'
import FilePreviewModal from '../../shared/components/FilePreviewModal'
import PriceModal from '../../shared/components/PriceModal'
import RejectModal from '../../shared/components/RejectModal'
import RestoreModal from '../../shared/components/RestoreModal'
import ConfirmationCard from '../../shared/components/ConfirmationCard'

import { formatFileSize } from '../../shared/utils/fileUtils'
import { useAuthStore } from '../../shared/auth/store'
import { useNotifications } from '../../shared/hooks/useNotifications'
import { useOrdersStore, useCachedOrderDetail, OrderDetail as CachedOrderDetail } from '../../shared/store/ordersStore'
import { useRealtimeSystem } from '../../shared/store/realtimeSync'
import OrderDetailLoader from '../../shared/components/OrderDetailLoader'
import { 
  getConfirmationState, 
  acceptConfirmation, 
  requestPriceChange, 
  cleanupOldConfirmations,
  ConfirmationState 
} from '../../shared/api/confirmationsApi'
import { getOrderDeadline, clearOrderDeadline, DeadlineInfo } from '../../shared/api/deadlineApi'
import '../../shared/styles/order-detail.css'
import { logger } from '../../shared/utils/logger'

interface OrderDetail {
  id: number
  title: string
  status: string
  preview_image?: string
  description?: string
  updated_at: string
  deadline?: string
  price?: string
  urgent?: boolean
  company_name?: string
  contact_name?: string
  // Информация о поданной цене
  submitted_price?: number
  submitted_delivery_time?: number
  submitted_comment?: string
  price_formatted?: string
  delivery_time_formatted?: string
  // Информация об отказе
  rejected_reason?: string
  rejected_at?: string
  rejected_company_name?: string
  // Информация о дедлайне
  completion_deadline_formatted?: string
  deadline_status?: string
  deadline_css_class?: string
  days_remaining?: number
  processing_types?: Array<{
    id: number
    name: string
    price?: string
    description?: string
  }>
  files?: Array<{
    id: number
    name: string
    url: string
    type: string
    size?: number
  }>
  comments?: Array<{
    id: number
    text: string
    created_at: string
    author: string
  }>
}

export const OrderDetailPage: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>()
  const navigate = useNavigate()
  const [order, setOrder] = useState<OrderDetail | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  
  // Подключаемся к глобальному кешу для обновления счетчиков
  const { updateOrderInGlobalCache } = useOrdersStore()
  
  // Хук для управления уведомлениями
  const { deleteNotificationsByOrder } = useNotifications()
  
  // 🆕 Хук для работы с кешированными деталями заказа
  const { getCachedOrder, cacheOrder, updateCachedOrder, isInCache } = useCachedOrderDetail(orderId || '')
  
  // 🔄 Инициализация системы реального времени
  useRealtimeSystem()


  
  // Состояние для модального окна предпросмотра файлов
  const [previewModal, setPreviewModal] = useState<{
    isOpen: boolean
    fileUrl: string
    fileName: string
  }>({
    isOpen: false,
    fileUrl: '',
    fileName: ''
  })
  
  // Модальные окна для действий
  const [showPriceModal, setShowPriceModal] = useState(false)
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [showRestoreModal, setShowRestoreModal] = useState(false)
  
  // Состояние для подтверждений
  const [confirmationState, setConfirmationState] = useState<ConfirmationState | null>(null)
  
  // Состояние дедлайна
  const [deadlineInfo, setDeadlineInfo] = useState<DeadlineInfo | null>(null)
  
  // Состояние для определения типа модального окна цены
  const [priceModalMode, setPriceModalMode] = useState<'submit' | 'edit' | 'change'>('submit')

  // 🔄 Загрузка информации о дедлайне
  const loadDeadlineInfo = async () => {
    if (!orderId) return
    
    try {
      const response = await getOrderDeadline(orderId)
      setDeadlineInfo(response.deadline)
    } catch (error) {
      logger.error('❌ Ошибка загрузки дедлайна:', error)
      // Не показываем ошибку пользователю, так как дедлайн может быть не установлен
    }
  }

  // 🔄 Загрузка состояния подтверждения
  const loadConfirmationState = async () => {
    if (!orderId) return
    
    try {
      const state = await getConfirmationState(orderId)
      setConfirmationState(state)
      
      // Обновляем кеш с информацией о подтверждении
      if (state.success && state.state && order) {
        const updateData = {
          confirmation_state: state.state,
          confirmation_updated_at: state.updated_at || new Date().toISOString()
        }
        
        // Обновляем локальное состояние
        setOrder(prev => prev ? { ...prev, ...updateData } : prev)
        
        // Обновляем кеш
        updateCachedOrder(updateData)
        updateOrderInGlobalCache(String(order.id), updateData)
      }
    } catch (error) {
      logger.error('Ошибка загрузки состояния подтверждения:', error)
      // Устанавливаем состояние по умолчанию при ошибке
      setConfirmationState({ success: true, state: 'pending' })
    }
  }

  // 🚀 Загрузка данных заказа с использованием кеша
  const loadOrder = async () => {
    if (!orderId) return
    
    try {
      setIsLoading(true)
      setError(null)
      
      // 🔍 Сначала проверяем кеш
      const cachedOrder = getCachedOrder()
      if (cachedOrder) {
        logger.log(`⚡ Заказ #${orderId} загружен из кеша (без запроса к серверу)`)
        // Приводим к локальному типу OrderDetail
        setOrder(cachedOrder as OrderDetail)
        
        // Небольшая задержка для плавности анимации (минимум 300ms)
        setTimeout(() => {
          setIsLoading(false)
        }, 300)
        return // Выходим, не делая запрос к серверу
      }
      
      // 📡 Если в кеше нет - загружаем с сервера
      logger.log(`📡 Загружаем заказ #${orderId} с сервера...`)
      const response = await http.get(`/orders/${orderId}`)
      
      if (response.data.success) {
        const loaded = response.data.order as OrderDetail
        setOrder(loaded)
        
        // 📦 Сохраняем в кеш для будущих обращений
        cacheOrder(loaded as CachedOrderDetail)
        
        // Автоматически отмечаем заказ как просмотренный
        if (response.data.order.status === 'new') {
          try {
            await markViewed(orderId)
            
            // 🔄 Обновляем глобальный кеш: меняем статус с 'new' на 'watching'
            updateOrderInGlobalCache(String(loaded.id), {
              status: 'watching',
              updated_at: new Date().toISOString()
            })
            
            // 🔄 Обновляем кеш деталей тоже
            updateCachedOrder({
              status: 'watching',
              updated_at: new Date().toISOString()
            })
            
            // Обновляем локальное состояние тоже
            setOrder(prev => prev ? { ...prev, status: 'watching' } : prev)
            
            logger.log(`✅ Заказ #${orderId} отмечен как просмотренный`)
            
            // 🔥 УВЕДОМЛЯЕМ О ТОМ ЧТО ЧАТ ГОТОВ К ИСПОЛЬЗОВАНИЮ
            // Это позволит ChatWidget корректно найти чат при первом нажатии
            window.dispatchEvent(new CustomEvent('orderChatReady', { 
              detail: { orderId: orderId }
            }))
            
          } catch (markError: any) {
            logger.error('Ошибка отметки просмотра:', markError)
            // Не блокируем загрузку заказа из-за ошибки отметки просмотра
          }
        }
      } else {
        setError(response.data.message || 'Ошибка загрузки заказа')
      }
    } catch (error: any) {
      logger.error('Ошибка загрузки заказа:', error)
      setError(error.response?.data?.message || 'Ошибка загрузки заказа')
    } finally {
      setIsLoading(false)
      
      // 🔔 Удаляем все уведомления по этому заказу при входе на страницу
      if (orderId) {
        try {
          await deleteNotificationsByOrder(orderId)
          logger.log(`🗑️ Удалены уведомления по заказу #${orderId} при просмотре`)
        } catch (notificationError) {
          logger.error('Ошибка удаления уведомлений:', notificationError)
          // Не блокируем загрузку из-за ошибки удаления уведомлений
        }
      }
    }
  }

  useEffect(() => {
    loadOrder()
    // Загружаем состояние подтверждения только для заказов со статусом pending_confirmation
    if (order?.status === 'pending_confirmation') {
      loadConfirmationState()
    }
    // Загружаем дедлайн для заказов с принятыми условиями
    if (order?.status === 'executing' || order?.status === 'defect' || order?.status === 'completed' || order?.status === 'rejected' || 
        order?.status === 'pending_confirmation') {
      loadDeadlineInfo()
    }
  }, [orderId])

  // Отдельный useEffect для загрузки состояния подтверждения при изменении статуса заказа
  useEffect(() => {
    if (order?.status === 'pending_confirmation') {
      // При получении статуса pending_confirmation сначала очищаем старые записи, затем загружаем состояние
      const initializePendingConfirmation = async () => {
        try {
          // Очищаем старые записи подтверждений
          await cleanupOldConfirmations(orderId!)
          // Очищаем старые дедлайны
          await clearOrderDeadline(orderId!)
          // Очищаем состояние дедлайна во фронтенде
          setDeadlineInfo(null)
          // Затем загружаем текущее состояние
          await loadConfirmationState()
        } catch (error) {
          logger.error('Ошибка при инициализации подтверждения:', error)
          // В случае ошибки все равно пытаемся загрузить состояние
          await loadConfirmationState()
        }
      }
      
      initializePendingConfirmation()
    } else if (order?.status && order.status !== 'pending_confirmation') {
      // Очищаем состояние подтверждения для других статусов
      setConfirmationState(null)
    }
  }, [order?.status])

  // Отдельный useEffect для загрузки дедлайна при изменении статуса заказа или состояния подтверждения
  useEffect(() => {
    const shouldLoadDeadline = order?.status === 'executing' || 
                              order?.status === 'defect' || 
                              order?.status === 'completed' || 
                              order?.status === 'rejected' || 
                              order?.status === 'pending_confirmation'
    
    if (shouldLoadDeadline) {
      loadDeadlineInfo()
    } else {
      setDeadlineInfo(null)
    }
  }, [order?.status, confirmationState?.state])

  // 🔄 Синхронизация с глобальным кешем для реактивности дизайна
  useEffect(() => {
    if (!orderId) return

    // Подписываемся на изменения в кеше деталей заказа
    const unsubscribe = useOrdersStore.subscribe(
      (state) => state.orderDetailsCache,
      (orderDetailsCache) => {
        const cachedOrder = orderDetailsCache.get(orderId)
        if (cachedOrder && order) {
          // Обновляем локальное состояние только если есть изменения
          if (cachedOrder.status !== order.status || 
              cachedOrder.updated_at !== order.updated_at ||
              cachedOrder.submitted_price !== order.submitted_price) {
            logger.log(`🔄 Синхронизация локального состояния заказа #${orderId} с кешем`)
            setOrder(prev => prev ? { ...prev, ...cachedOrder } as OrderDetail : cachedOrder as OrderDetail)
          }
        }
      }
    )

    return unsubscribe
  }, [orderId, order?.status, order?.updated_at, order?.submitted_price])

  // Обработчики действий
  const handleAction = async (action: string, actionFn: () => Promise<any>, additionalData?: any) => {
    try {
      setActionLoading(action)
      const result = await actionFn()
      
      // 🔄 Обновляем глобальный кеш в зависимости от действия
      if (orderId && order && result?.success !== false) {
        let statusUpdate: string | undefined
        let cacheUpdates: any = {
          updated_at: new Date().toISOString()
        }
        
        switch (action) {
          case 'takeOrder':
            statusUpdate = 'calculating'
            break
          case 'submitPrice':
            statusUpdate = 'price_given'
            // Добавляем информацию о цене в кеш
            if (additionalData) {
              cacheUpdates.submitted_price = additionalData.price
              cacheUpdates.submitted_delivery_time = additionalData.delivery_time
              cacheUpdates.submitted_comment = additionalData.comment
              
              // Добавляем форматированные поля тоже
              cacheUpdates.price_formatted = `${additionalData.price.toLocaleString('ru-RU')} руб.`
              
              if (additionalData.delivery_time === 1) {
                cacheUpdates.delivery_time_formatted = "1 рабочий день"
              } else if (additionalData.delivery_time < 5) {
                cacheUpdates.delivery_time_formatted = `${additionalData.delivery_time} рабочих дня`
              } else {
                cacheUpdates.delivery_time_formatted = `${additionalData.delivery_time} рабочих дней`
              }
            }
            break
          case 'reject':
            statusUpdate = 'rejected'
            // Добавляем информацию об отказе в кеш
            if (additionalData?.reason) {
              cacheUpdates.rejected_reason = additionalData.reason
              cacheUpdates.rejected_at = new Date().toISOString()
              // Получаем имя компании из текущего заказа или используем заглушку
              cacheUpdates.rejected_company_name = order?.company_name || 'Подрядчик'
            }
            break
          case 'restore':
            statusUpdate = 'calculating'
            // Очищаем информацию об отказе
            cacheUpdates.rejected_reason = null
            cacheUpdates.rejected_at = null
            cacheUpdates.rejected_company_name = null
            break
        }
        
        if (statusUpdate) {
          cacheUpdates.status = statusUpdate
          
          // Используем ID заказа из состояния для точного соответствия
          updateOrderInGlobalCache(String(order.id), cacheUpdates)
          
          // 🔄 Обновляем кеш деталей тоже
          updateCachedOrder(cacheUpdates)
          
          // 🔄 Обновляем локальное состояние тоже для мгновенного отображения
          setOrder(prev => prev ? { ...prev, ...cacheUpdates } : prev)

          logger.log(`🔄 Заказ #${order.id} обновлен в кешах и локальном состоянии:`, cacheUpdates)
        }
      }
      
      // Перезагружаем данные с сервера через задержку
      // чтобы дать серверу время сохранить изменения и пользователю увидеть обновления
      setTimeout(async () => {
        logger.log('🔄 Перезагружаем данные с сервера для синхронизации...')
        await loadOrder()
      }, 2000)
    } catch (error: any) {
      logger.error(`Ошибка выполнения действия ${action}:`, error)
      alert(`Ошибка: ${error.response?.data?.message || error.message}`)
    } finally {
      setActionLoading(null)
    }
  }

  const handleSubmitPrice = async (data: { price: number; delivery_time: number; comment: string }) => {
    if (!orderId) return
    
    await handleAction('submitPrice', async () => {
      await submitPrice(orderId, data.price, data.comment, data.delivery_time)
    }, data) // Передаем данные цены для обновления кеша
  }

  const handleReject = async (reason: string) => {
    if (!orderId) return
    
    await handleAction('reject', async () => {
      await rejectOrder(orderId, reason)
    }, { reason })
  }

  const handleRestore = async (reason: string) => {
    if (!orderId) return
    
    await handleAction('restore', async () => {
      await OrderActionsApi.restoreOrder(orderId, reason)
    }, { reason })
  }

  const handleEditPrice = () => {
    setPriceModalMode('edit')
    setShowPriceModal(true)
  }

  
  // Обработчики для подтверждений
  const handleAcceptConfirmation = async () => {
    if (!orderId) return
    
    try {
      setActionLoading('accept_confirmation')
      const result = await acceptConfirmation(orderId)
      
      if (result.success) {
        // Обновляем состояние подтверждения
        setConfirmationState({ success: true, state: 'accepted' })
        
        // Обновляем статус заказа
        setOrder(prev => prev ? { ...prev, status: 'executing', confirmation_state: 'accepted', confirmation_updated_at: new Date().toISOString() } : prev)
        
        // Обновляем кеш с информацией о подтверждении
        const updateData = { 
          status: 'executing', 
          updated_at: new Date().toISOString(),
          confirmation_state: 'accepted' as const,
          confirmation_updated_at: new Date().toISOString()
        }
        updateCachedOrder(updateData)
        updateOrderInGlobalCache(String(order?.id), updateData)
        
        // 🔄 Загружаем дедлайн после принятия условий
        await loadDeadlineInfo()
        
        logger.log('✅ Условия приняты, заказ запущен в работу')
      } else {
        alert(result.message || 'Ошибка при принятии условий')
      }
    } catch (error: any) {
      logger.error('Ошибка при принятии условий:', error)
      alert('Ошибка при принятии условий')
    } finally {
      setActionLoading(null)
    }
  }

  const handleRequestPriceChange = () => {
    setPriceModalMode('change')
    setShowPriceModal(true)
  }

  const handleUniversalPriceSubmit = async (data: { price: number; delivery_time: number; comment: string }) => {
    if (!orderId) return
    
    try {
      if (priceModalMode === 'change') {
        // Запрос изменения цены при подтверждении
        setActionLoading('request_price_change')
        const result = await requestPriceChange({
          order_id: orderId,
          price: data.price,
          delivery_time: data.delivery_time,
          comment: data.comment
        })
        
        if (result.success) {
          // Обновляем состояние подтверждения
          setConfirmationState({ success: true, state: 'modified' })
          
          // Обновляем статус заказа на 'price_given' (оценен) и данные о цене
          setOrder(prev => prev ? { 
            ...prev, 
            status: 'price_given', 
            confirmation_state: 'modified', 
            confirmation_updated_at: new Date().toISOString(),
            submitted_price: data.price,
            submitted_delivery_time: data.delivery_time,
            submitted_comment: data.comment,
            // Очищаем форматированные поля, чтобы использовались новые значения
            price_formatted: undefined,
            delivery_time_formatted: undefined
          } : prev)
          
          // Обновляем кеш с информацией о подтверждении и новой цене
          const updateData = { 
            status: 'price_given', 
            updated_at: new Date().toISOString(),
            confirmation_state: 'modified' as const,
            confirmation_updated_at: new Date().toISOString(),
            submitted_price: data.price,
            submitted_delivery_time: data.delivery_time,
            submitted_comment: data.comment,
            // Очищаем форматированные поля, чтобы использовались новые значения
            price_formatted: undefined,
            delivery_time_formatted: undefined
          }
          updateCachedOrder(updateData)
          updateOrderInGlobalCache(String(order?.id), updateData)
          
          logger.log('✅ Запрос на изменение цены отправлен')
          
          // Закрываем модальное окно
          setShowPriceModal(false)
        } else {
          alert(result.message || 'Ошибка при отправке запроса на изменение')
        }
      } else {
        // Обычная подача/изменение цены
        await handleSubmitPrice(data)
      }
    } catch (error: any) {
      logger.error('Ошибка при отправке цены:', error)
      alert('Ошибка при отправке цены')
    } finally {
      setActionLoading(null)
    }
  }

  // Функция форматирования даты (защита от Invalid Date)
  const formatDate = (dateString: string) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return '-'
    return date.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  // Функция определения цвета статуса
  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      new: '#28a745',
      watching: '#17a2b8',
      calculating: '#ffc107',
      price_given: '#007bff',
      executing: '#6f42c1',
      defect: '#dc3545',
      rejected: '#6c757d',
      archived: '#6c757d',
      completed: '#28a745'
    }
    return colors[status] || '#6c757d'
  }

  // Проверка типа файла на изображение
  const isImageFile = (file: { type?: string; name: string }) => {
    const imageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/bmp', 'image/webp', 'image/tiff']
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'tiff', 'tif']
    
    if (file.type && imageTypes.includes(file.type.toLowerCase())) {
      return true
    }
    
    const extension = file.name.split('.').pop()?.toLowerCase()
    return extension ? imageExtensions.includes(extension) : false
  }

  // Функция для форматирования описания заказа
  const formatDescription = (description: string) => {
    if (!description) return '—'
    
    // Ищем "Материал исполнителя:" или "Материал:" в начале строки и делаем их жирными
    const materialMatch = description.match(/^(Материал(?:\s+исполнителя)?:?\s*)(.*)/i)
    if (materialMatch) {
      return (
        <>
          <b>{materialMatch[1]}</b>
          {materialMatch[2]}
        </>
      )
    }
    
    return description
  }

  // Действия с файлами
  const handlePreviewFile = async (fileName: string) => {
    if (!orderId) return
    
    // Проверяем авторизацию через единую систему
    const authState = useAuthStore.getState()
    const token = authState.token
    
    if (!token || !authState.isAuthenticated) {
      alert('Ошибка: необходима авторизация')
      return
    }
    
    // Генерируем URL напрямую как в оригинальном мини-приложении, но с нашим токеном
    const previewUrl = `/api/files/preview-order-file/${orderId}?file_name=${encodeURIComponent(fileName)}&token=${encodeURIComponent(token)}`
    
    // Открываем файл в модальном окне с блюром
    setPreviewModal({
      isOpen: true,
      fileUrl: previewUrl,
      fileName: fileName
    })
  }

  const closePreviewModal = () => {
    setPreviewModal({
      isOpen: false,
      fileUrl: '',
      fileName: ''
    })
  }

  const handlePreviewDownload = async () => {
    if (!orderId || !previewModal.fileName) return
    
    // Проверяем авторизацию через единую систему
    const authState = useAuthStore.getState()
    const token = authState.token
    
    if (!token || !authState.isAuthenticated) {
      alert('Ошибка: необходима авторизация')
      return
    }
    
    try {
      // Используем тот же эндпоинт, но с параметром download=1
      const downloadUrl = `/api/files/preview-order-file/${orderId}?file_name=${encodeURIComponent(previewModal.fileName)}&token=${encodeURIComponent(token)}&download=1`
      
      // Создаем невидимую ссылку для скачивания
      const a = document.createElement('a')
      a.href = downloadUrl
      a.download = previewModal.fileName
      a.style.display = 'none'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    } catch (e: any) {
      logger.error('Ошибка скачивания файла:', e)
      alert('Не удалось скачать файл')
    }
  }

  const handleDownloadFile = async (fileName: string) => {
    if (!orderId) return
    
    // Проверяем авторизацию через единую систему
    const authState = useAuthStore.getState()
    const token = authState.token
    
    if (!token || !authState.isAuthenticated) {
      alert('Ошибка: необходима авторизация')
      return
    }
    
    try {
      // Используем тот же эндпоинт, но с параметром download=1
      const downloadUrl = `/api/files/preview-order-file/${orderId}?file_name=${encodeURIComponent(fileName)}&token=${encodeURIComponent(token)}&download=1`
      
      // Создаем невидимую ссылку для скачивания
      const a = document.createElement('a')
      a.href = downloadUrl
      a.download = fileName
      a.style.display = 'none'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    } catch (e: any) {
      logger.error('Ошибка скачивания файла:', e)
      alert('Не удалось скачать файл')
    }
  }

  const handleShareFile = async (fileName: string) => {
    if (!orderId) return
    try {
      const res = await shareSingleOrderFile(orderId, fileName)
      await navigator.clipboard.writeText(res.url)
      alert('Ссылка скопирована в буфер обмена')
    } catch (e) {
      alert('Не удалось сгенерировать ссылку')
    }
  }



  const handleDownloadAllFiles = async () => {
    if (!orderId || !order?.files?.length) return
    
    // Проверяем авторизацию через единую систему
    const authState = useAuthStore.getState()
    const token = authState.token
    
    if (!token || !authState.isAuthenticated) {
      alert('Ошибка: необходима авторизация')
      return
    }

    try {
      let successCount = 0
      let failedFiles: string[] = []

      // Скачиваем все файлы по очереди
      for (const file of order.files) {
        try {
          // Используем тот же эндпоинт, что и для одиночного скачивания
          const downloadUrl = `/api/files/preview-order-file/${orderId}?file_name=${encodeURIComponent(file.name)}&token=${encodeURIComponent(token)}&download=1`
          
          // Создаем невидимую ссылку для скачивания
          const a = document.createElement('a')
          a.href = downloadUrl
          a.download = file.name
          a.style.display = 'none'
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
          
          successCount++
          
          // Небольшая задержка между скачиваниями чтобы не перегружать браузер
          if (order.files.length > 1) {
            await new Promise(resolve => setTimeout(resolve, 500))
          }
        } catch (e) {
          logger.error(`Ошибка скачивания файла ${file.name}:`, e)
          failedFiles.push(file.name)
        }
      }

      // Показываем результат
      if (failedFiles.length === 0) {
        alert(`Успешно начато скачивание ${successCount} файлов`)
      } else {
        alert(`Скачано ${successCount} файлов. Не удалось скачать: ${failedFiles.join(', ')}`)
      }
    } catch (e: any) {
      logger.error('Ошибка массового скачивания:', e)
      alert('Не удалось скачать файлы')
    }
  }

  // Функция определения названия статуса
  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      new: 'Новый',
      watching: 'На просмотре',
      calculating: 'На просчете',
      price_given: 'Оценен',
      executing: 'Выполняется',
      defect: 'Брак',
      rejected: 'Отказ',
      archived: 'Архив',
      completed: 'Выполнен'
    }
    return labels[status] || status
  }

  if (isLoading) {
    return <OrderDetailLoader message="Загрузка заказа..." />
  }

  if (error || !order) {
    return (
      <div style={{
        padding: '20px',
        textAlign: 'center'
      }}>
        <div style={{
          backgroundColor: '#f8d7da',
          color: '#721c24',
          padding: '16px',
          borderRadius: '8px',
          border: '1px solid #f5c2c7',
          marginBottom: '20px'
        }}>
          ❌ {error || 'Заказ не найден'}
        </div>
        <button
          onClick={() => navigate('/orders')}
          style={{
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            padding: '8px 16px',
            cursor: 'pointer'
          }}
        >
          ← Вернуться к списку заказов
        </button>
      </div>
    )
  }

  return (
    <div className="relative">
      {/* Overlay загрузки для действий */}
      {actionLoading && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 shadow-xl">
            <div className="flex flex-col items-center space-y-4">
              {/* Мини версия анимации */}
              <div className="relative w-12 h-12 flex items-center justify-center">
                <div className="absolute w-12 h-12 rounded-full border-2 border-dashed border-blue-400 animate-[spin_8s_linear_infinite]"></div>
                <div className="absolute w-8 h-8 rounded-full border-2 border-dotted border-indigo-500 animate-[spin_5s_linear_infinite_reverse]"></div>
                <div className="absolute w-4 h-4 rounded-full border-2 border-slate-600 animate-[spin_3s_linear_infinite]"></div>
                <div className="absolute w-1 h-1 rounded-full bg-blue-600 animate-pulse"></div>
              </div>
              <p className="text-sm font-medium text-slate-700">
                {actionLoading === 'takeOrder' && 'Взятие в просчет...'}
                {actionLoading === 'submitPrice' && 'Отправка цены...'}
                {actionLoading === 'reject' && 'Отказ от заказа...'}
                {actionLoading === 'request_price_change' && 'Изменение цены...'}
                {actionLoading === 'accept_confirmation' && 'Принятие условий...'}
                {!['takeOrder', 'submitPrice', 'reject', 'request_price_change', 'accept_confirmation'].includes(actionLoading) && 'Выполнение действия...'}
              </p>
            </div>
          </div>
        </div>
      )}
      
    <OrderDetailAdaptive
      title={order.title || `Заказ #${order.id}`}
      previewUrl={order.preview_image}
      details={[
        <>{formatDescription(order.description || '')}</>,
        <><b>Последнее обновление:</b> {formatDate(order.updated_at)}</>,
      ]}
      processingTypes={(order.processing_types || []).map(pt => pt.name)}
      files={(order.files || []).map(f => ({ 
        name: f.name, 
        size: f.size ? formatFileSize(f.size) : '', 
        type: f.type 
      }))}
      quantity={order.files?.length}
      status={order.status}
      orderId={orderId!}
      submittedPrice={order.submitted_price}
      submittedDeliveryTime={order.submitted_delivery_time}
      submittedComment={order.submitted_comment}
      priceFormatted={order.price_formatted}
      deliveryTimeFormatted={order.delivery_time_formatted}
      rejectedReason={order.rejected_reason}
      rejectedAt={order.rejected_at}
      rejectedCompanyName={order.rejected_company_name}
      confirmationState={confirmationState?.state || 'pending'}
      showConfirmationCard={order.status === 'pending_confirmation' && confirmationState?.success}
      // Передаем информацию о дедлайне
      deadlineFormatted={deadlineInfo?.completion_deadline_formatted}
      deadlineStatus={deadlineInfo?.status_text}
      deadlineCssClass={deadlineInfo?.css_class}
      daysRemaining={deadlineInfo?.days_remaining}
      onDownloadFile={(filename) => handleDownloadFile(filename)}
      onDownloadAll={handleDownloadAllFiles}
      // Для pending_confirmation, executing, defect и completed не передаем обработчики действий
      onTake={order.status !== 'pending_confirmation' && order.status !== 'executing' && order.status !== 'defect' && order.status !== 'completed' ? () => handleAction('takeOrder', () => takeOrder(orderId!)) : undefined}
      onReject={order.status !== 'pending_confirmation' && order.status !== 'executing' && order.status !== 'defect' && order.status !== 'completed' ? () => setShowRejectModal(true) : undefined}
      onRestore={order.status !== 'pending_confirmation' && order.status !== 'executing' && order.status !== 'defect' && order.status !== 'completed' ? () => setShowRestoreModal(true) : undefined}
      onSubmitPrice={order.status !== 'pending_confirmation' && order.status !== 'executing' && order.status !== 'defect' && order.status !== 'completed' ? () => { setPriceModalMode('submit'); setShowPriceModal(true) } : undefined}
      onEditPrice={order.status !== 'pending_confirmation' && order.status !== 'executing' && order.status !== 'defect' && order.status !== 'completed' ? handleEditPrice : undefined}
      onComplete={undefined}
      onDefect={undefined}
      onFix={undefined}
      onBack={() => navigate('/orders')}
      onPreviewFile={(filename) => handlePreviewFile(filename)}
      onAcceptConfirmation={handleAcceptConfirmation}
      onRequestPriceChange={handleRequestPriceChange}
    />

      {/* Модальные окна */}
      <PriceModal
        isOpen={showPriceModal}
        onClose={() => setShowPriceModal(false)}
        onSubmit={handleUniversalPriceSubmit}
        orderId={orderId!}
        orderTitle={order?.title}
        isEdit={priceModalMode === 'edit' || priceModalMode === 'change'}
        initialPrice={order?.submitted_price}
        initialDeliveryTime={order?.submitted_delivery_time}
        initialComment={priceModalMode === 'change' ? '' : ''} // Всегда пустой комментарий для нового предложения
      />

      <RejectModal
        isOpen={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        onSubmit={handleReject}
        orderId={orderId!}
        orderTitle={order?.title}
      />

      <RestoreModal
        isOpen={showRestoreModal}
        onClose={() => setShowRestoreModal(false)}
        onSubmit={handleRestore}
        orderId={orderId!}
        orderTitle={order?.title}
      />



      {/* Модальное окно для предпросмотра файлов */}
      <FilePreviewModal
        isOpen={previewModal.isOpen}
        onClose={closePreviewModal}
        fileUrl={previewModal.fileUrl}
        fileName={previewModal.fileName}
        onDownload={handlePreviewDownload}
      />
    </div>
  )
}
